import { ApolloClient } from '@apollo/client'
import {
  CfRetailCategoryFilter,
  RetailCategoryCollectionDocument,
  RetailCategoryCollectionWithImageDocument,
} from '@scentregroup/shared/graphql'
import { slugify } from '@scentregroup/shared/helpers'
import { isDefinedAndNotNull } from '@scentregroup/shared/lib'
import { ContentPageData } from '../../../lib/get-content-detail/content-page'
import {
  makePopularCategoriesFromStoresContent,
  PopularCategory,
  RetailCategory,
} from './types'

export async function fetchRetailCategoryHeirarchy<TCacheShape>(
  apollo: Pick<ApolloClient<TCacheShape>, 'query'>,
  country: 'Australia' | 'New Zealand'
): Promise<RetailCategory[]> {
  const { data } = await apollo.query({
    context: { version: 2 },
    query: RetailCategoryCollectionDocument,
    variables: {
      filter: { country, retailCategoryIds: null },
    },
  })
  if (!data?.sgRetailersFacetRetailerCategories) {
    return []
  }
  const allRetailCategories = data.sgRetailersFacetRetailerCategories
    .filter(isDefinedAndNotNull)
    .filter(item => item.title)
    .map<
      RetailCategory & {
        parent?: string
      }
    >(item => ({
      id: item.sys.id,
      title: item.title || '',
      shortDescription: item.shortDescription,
      slug: slugify(item.title),
      level: item.level || 0,
      ...(item.parentCategory?.sys?.id && {
        parent: item.parentCategory.sys.id,
      }),
    }))
  const byTitle = (a: { title: string }, b: { title: string }): number =>
    a.title > b.title ? 1 : -1

  const withChildren = (item: RetailCategory): RetailCategory => ({
    ...item,
    children: allRetailCategories
      .filter(child => child.parent === item.id)
      .sort(byTitle)
      .map(withChildren),
  })

  return allRetailCategories
    .filter(item => item.level === 1)
    .sort(byTitle)
    .map(withChildren)
}

export async function fetchPopularCategories<TCacheShape>(
  apollo: Pick<ApolloClient<TCacheShape>, 'query'>,
  content: ContentPageData
): Promise<PopularCategory[] | null> {
  const popularCategories = makePopularCategoriesFromStoresContent(content)
  if (popularCategories === null) {
    return null
  }

  const id_in = popularCategories.map(({ id }) => id)
  // graphql-codegen is broken! `InputMaybe<T> = T | null` # what? that's not helpful
  const where = { sys: { id_in } } as CfRetailCategoryFilter
  const { data } = await apollo.query({
    context: { version: 2 },
    query: RetailCategoryCollectionWithImageDocument,
    variables: { where },
  })
  if (!data) {
    return popularCategories
  }
  return popularCategories.map(popularCategory => {
    const retailCategory = data.cfRetailCategoryCollection?.items.find(
      item => item?.sys.id === popularCategory.id
    )
    if (!retailCategory || !retailCategory.title) {
      return popularCategory
    }
    const imageSet = retailCategory.heroImageSet
    return {
      title: retailCategory.title,
      slug: slugify(retailCategory.title),
      imageUrl: imageSet?.square?.url ?? imageSet?.master?.url ?? null,
      ...popularCategory,
    }
  })
}
